import React, { useState } from 'react';
import { AlertCircle, Check, KeyRound } from 'lucide-react';
import { useAuth, isViewingAs, VIEWING_AS_NOTE } from '../context/AuthContext';
import PasswordInput from './PasswordInput';

/**
 * B-65 addition (Stuart, 15 Oct 2026): in-app "Change password", or "Set a
 * password" for an account that has none (Apple/Google-only, or one whose
 * password B-53 removed). POST /auth/change-password: the server signs every
 * other device out and gives this one a new token.
 *
 * Setting a first password needs a CONFIRMED email (server rule, see
 * routes/auth.js): a session alone mustn't be able to add a way in.
 *
 * onNewToken(token) — the staff portal passes its own, so its axios gets the
 * new token too (the member app's is handled by AuthContext).
 */
const PasswordSection = ({ onNewToken }) => {
  const { user, changePassword, impersonating } = useAuth();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  // B-65 review: never on a staff "view as" session (the server refuses it too).
  if (isViewingAs(impersonating)) {
    return <div className="account-card"><p className="muted" style={{ fontSize: 13, margin: 0 }}>{VIEWING_AS_NOTE}</p></div>;
  }
  const hasPassword = user.hasPassword !== false; // older servers don't send it: assume a password
  const canSet = hasPassword || user.emailVerified === true;

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setDone('');
    if (next.length < 8) { setError('Your new password must be at least 8 characters.'); return; }
    if (next !== again) { setError('The new passwords don\'t match.'); return; }
    setBusy(true);
    try {
      const res = await changePassword({ currentPassword: hasPassword ? current : undefined, newPassword: next });
      if (onNewToken) onNewToken(res.token);
      setDone(res.passwordSet
        ? 'Password set. You can now also sign in with your email and password. Other devices have been signed out.'
        : 'Password changed. Other devices have been signed out.');
      setOpen(false); setCurrent(''); setNext(''); setAgain('');
    } catch (err) {
      setError(err?.response?.data?.error || 'We couldn\'t change your password. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="account-card password-section">
      {done && <p className="password-done" role="status" style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: '#2D7A4F', margin: '0 0 12px' }}><Check size={15} /> {done}</p>}
      {!canSet ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          You sign in with Apple or Google. To add a password, confirm your email first.
        </p>
      ) : !open ? (
        <>
          <p className="muted" style={{ fontSize: 13, margin: '0 0 12px' }}>
            {hasPassword
              ? 'Change the password you use to sign in. Other devices will be signed out.'
              : 'You sign in with Apple or Google. Add a password to sign in with your email too.'}
          </p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(true); setDone(''); }}>
            <KeyRound size={14} /> {hasPassword ? 'Change password' : 'Set a password'}
          </button>
        </>
      ) : (
        <form onSubmit={submit} className="password-form" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {hasPassword && (
            <div className="form-group">
              <label className="label" htmlFor="pw-current">Current password</label>
              <PasswordInput id="pw-current" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} required />
            </div>
          )}
          <div className="form-group">
            <label className="label" htmlFor="pw-new">New password</label>
            <PasswordInput id="pw-new" autoComplete="new-password" placeholder="Min 8 characters" value={next} onChange={e => setNext(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="label" htmlFor="pw-again">New password again</label>
            <PasswordInput id="pw-again" autoComplete="new-password" value={again} onChange={e => setAgain(e.target.value)} required />
          </div>
          {error && <p className="form-error" role="alert" style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}><AlertCircle size={14} /> {error}</p>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {busy ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setError(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PasswordSection;
